import { useEffect, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../lib/use-item-seen-bi';
import { CREDITS_WIDGET_FIELD, CREDITS_WIDGET_TYPE } from '../../../lib/credits-widget-bi';
import { useEarnCreditsOfferEnabled } from '../../../lib/earn-credits-offer-flag';
import { earnOfferEntryPoint, showEarnCreditsOffer, type EarnOfferEntry } from '../../../lib/earn-credits-offer';
import { useRewardRules } from '../../../lib/use-reward-rules';
import { ShareAppModal } from '../../settings/shared/ShareAppModal';
import { Button, Typography } from '../ui';
import styles from './EarnCreditsOffer.module.css';

/**
 * The "earn credits instead" offer at the out-of-credits moments — ONE component behind ONE gate,
 * rendered at three entry points (the rail/drawer credits card in the red band, a routine paused
 * for credits, and the Max+ buy-credits modal/sheet).
 *
 * It is an ENTRY POINT, not a second claim UI: the button opens the existing Share-the-app modal,
 * which already hosts `EarnCreditsSection` (follow → post → screenshot). Nothing about the reward
 * flow is duplicated here.
 *
 * It is deliberately SECONDARY everywhere. Upgrade / Check out keep their place, their skin and
 * their own BI events (`upgrade_credits`, `get_more_credits`, `buy_credits_checkout`) untouched —
 * those are the conversion baseline this offer is measured against. This one reports its own events,
 * each tagged with `entry_point`, so cannibalization is a query rather than a guess: a `loadFinish`
 * when the gate resolves it into the tree, an `itemSeen` (198:6) when it actually enters the viewport,
 * and a click (`earn_credits_offer`). The last two also carry `widget_type: ooc` — the credits funnel's
 * own join key, shared with the in-chat card and both phones.
 *
 * Whether to show it is `showEarnCreditsOffer` (lib/earn-credits-offer.ts) — import-free and tested
 * on its own. The usage band comes from the pinned `creditsUsageLevel` helper, never re-derived.
 */
export function EarnCreditsOffer({
  entry,
  pageName,
  variant = 'desktop',
  size = 'small',
  className,
}: {
  entry: EarnOfferEntry;
  /** The BI `page_name` of the surface the offer is rendered on. */
  pageName: string;
  /** Which share layout to open — the phone screen on the narrow surfaces. */
  variant?: 'desktop' | 'mobile';
  size?: 'tiny' | 'small';
  className?: string;
}) {
  const { t } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi, logLoadFinish } = useGenericBi();
  const gateEnabled = useEarnCreditsOfferEnabled();
  const [open, setOpen] = useState(false);

  const show = showEarnCreditsOffer(gateEnabled, entry);
  const entryPoint = earnOfferEntryPoint(entry);
  // Only the buy-credits line carries an amount, so only it reads the served rules.
  const withLine = entryPoint === 'buy_credits';
  const { socialReward } = useRewardRules({ enabled: show && withLine });

  // One impression per surface, once the gate has actually resolved the offer into view.
  useEffect(() => {
    if (!show) return;
    logLoadFinish('earn-credits-offer', { isSuccessful: true }, { additionalFields: { entry_point: entryPoint } });
    // logLoadFinish is a fresh ref each render; excluded so this fires on the offer appearing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, entryPoint]);

  // The credits funnel's own impression (198:6), which is NOT the loadFinish above: that one fires when
  // the gate resolves, wherever the offer happens to be, and it carries the entry point for the offer's
  // own analysis. This one fires when a human could actually see it, and carries the widget type the
  // funnel joins on. Both are wanted; neither substitutes for the other.
  const seenRef = useItemSeenBi('earn-credits-offer', {
    additionalFields: { [CREDITS_WIDGET_FIELD]: CREDITS_WIDGET_TYPE.outOfCredits, entry_point: entryPoint },
  });

  if (!show) return null;

  const onClick = () => {
    logPageClickBi(
      { page_name: pageName, button_name: 'earn_credits_offer' },
      {
        componentName: 'earn-credits-offer',
        additionalFields: { entry_point: entryPoint, [CREDITS_WIDGET_FIELD]: CREDITS_WIDGET_TYPE.outOfCredits },
      },
    );
    setOpen(true);
  };

  return (
    <span ref={seenRef} className={`${styles.offer} ${withLine ? styles.stacked : ''} ${className ?? ''}`}>
      {withLine && (
        <Typography variant="p3" component="p" color="secondary">
          {/* The amount is SERVED. When the rules haven't loaded the sentence simply drops the
              number rather than promising one the program may not pay. */}
          {socialReward
            ? t('earnOffer.buyCredits.line', { productName, credits: socialReward.credits })
            : t('earnOffer.buyCredits.lineNoAmount', { productName })}
        </Typography>
      )}
      <Button size={size} variant="outlined" onClick={onClick}>
        {t('earnOffer.cta')}
      </Button>
      <ShareAppModal open={open} onClose={() => setOpen(false)} variant={variant} />
    </span>
  );
}
